/**
 * ForceEditPanel Store - Refrakt-based store for the force edit panel
 *
 * This store manages force edit form state:
 * - Form fields (title, description, certainty, impact, etc.)
 * - Autosave logic with debouncing
 * - Delete confirmation UI state
 * - Save/delete API calls via effects
 *
 * Communication with parent:
 * - Component emits DOM events for major changes
 */
import { pipe, store, updateUnknown } from "refrakt";
import { fx } from "refrakt/middleware/fx.js";
import { logger } from "refrakt/middleware/logger.js";
import * as Config from "../client-config.ts";
import { customEvent } from "../util/event.ts";
import * as Logger from "../services/logger.ts";
import { type ForceNote } from "../../models/note/force.ts";
import { type ForceCategory } from "../../models/force-category.ts";
import { type Arc } from "../../models/arc.ts";
import { type PaceLayer } from "../../models/pace-layer.ts";
import { type Ulid } from "../../models/ulid.ts";
import { type Slug } from "../../models/slug.ts";
import { nowIsoString } from "../../util/date.ts";
import * as NotesService from "../services/notes.ts";
import { sleep } from "../../util/sleep.ts";
import { renderRoute } from "../../util/route.ts";

const log = Logger.create({ source: "client/force-editor" });

export type Model = {
  /** Auth token for API calls - set by component */
  accessToken: string | undefined;
  saveToken: number;
  slug: string | undefined;
  projectId: string | undefined;
  type: string;
  title: string;
  description: string;
  certainty: number;
  impact: number;
  category: ForceCategory | undefined;
  arc: Arc | undefined;
  paceLayer: PaceLayer | undefined;
  tags: string[];
  sourceSlugs: Slug[];
  sourceUrls: string[];
  confidence: number;
  authorUserId: string | undefined;
  authorAgentId: string | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isConfirmDeleteMenuOpen: boolean;
  /** Last error message */
  lastError: string | undefined;
};

export const model = ({
  accessToken = undefined,
  saveToken = 0,
  slug = undefined,
  projectId = undefined,
  type = "",
  title = "",
  description = "",
  certainty = 0.5,
  impact = 0.5,
  category = undefined,
  arc = undefined,
  paceLayer = undefined,
  tags = [],
  sourceSlugs = [],
  sourceUrls = [],
  confidence = 0.5,
  authorUserId = undefined,
  authorAgentId = undefined,
  createdAt = undefined,
  updatedAt = undefined,
  isLoading = false,
  isSaving = false,
  isDeleting = false,
  isConfirmDeleteMenuOpen = false,
  lastError = undefined,
}: Partial<Model> = {}): Model => {
  return {
    accessToken,
    saveToken,
    slug,
    projectId,
    type,
    title,
    description,
    certainty,
    impact,
    category,
    arc,
    paceLayer,
    tags,
    sourceSlugs,
    sourceUrls,
    confidence,
    authorUserId,
    authorAgentId,
    createdAt,
    updatedAt,
    isLoading,
    isSaving,
    isDeleting,
    isConfirmDeleteMenuOpen,
    lastError,
  };
};

export type SetForceAction = {
  type: "set-force";
  force: ForceNote;
};

export type SetAccessTokenAction = {
  type: "set-access-token";
  accessToken: string | undefined;
};

export type SetTitleAction = {
  type: "set-title";
  value: string;
  accessToken: string | undefined;
};

export type SetDescriptionAction = {
  type: "set-description";
  value: string;
  accessToken: string | undefined;
};

export type SetCertaintyAction = {
  type: "set-certainty";
  value: number;
  accessToken: string | undefined;
};

export type SetImpactAction = {
  type: "set-impact";
  value: number;
  accessToken: string | undefined;
};

export type SetCategoryAction = {
  type: "set-category";
  value: ForceCategory | undefined;
  accessToken: string | undefined;
};

export type SetArcAction = {
  type: "set-arc";
  value: Arc | undefined;
  accessToken: string | undefined;
};

export type SetPaceLayerAction = {
  type: "set-pace-layer";
  value: PaceLayer | undefined;
  accessToken: string | undefined;
};

export type ScheduleSaveAction = {
  type: "schedule-save";
  accessToken: string | undefined;
};

export type CheckSaveAction = {
  type: "check-save";
  saveToken: number;
  accessToken: string | undefined;
};

export type ExecuteSaveAction = {
  type: "execute-save";
  forceNote: ForceNote;
  accessToken: string | undefined;
};

export type SaveSuccessAction = {
  type: "save-success";
  slug: Slug;
};

export type SaveErrorAction = {
  type: "save-error";
  error: string;
};

export type ToggleDeleteAlertAction = {
  type: "toggle-delete-alert";
  show: boolean;
};

export type RequestDeleteAction = {
  type: "request-delete";
  slug: Slug | undefined;
  projectId: Ulid | undefined;
};

export type SetLoadingAction = {
  type: "set-loading";
  isLoading: boolean;
};

export type LoadForceAction = {
  type: "load-force";
  slug: Slug;
  projectId: Ulid;
  accessToken: string | undefined;
};

export type LoadForceSuccessAction = {
  type: "load-force-success";
  force: ForceNote;
};

export type LoadForceNotFoundAction = {
  type: "load-force-not-found";
  slug: Slug;
  projectId: Ulid;
};

export type LoadForceErrorAction = {
  type: "load-force-error";
  error: string;
  slug: Slug;
  projectId: Ulid;
};

export type ResetAction = {
  type: "reset";
};

export type Action =
  | SetForceAction
  | SetAccessTokenAction
  | SetTitleAction
  | SetDescriptionAction
  | SetCertaintyAction
  | SetImpactAction
  | SetCategoryAction
  | SetArcAction
  | SetPaceLayerAction
  | ScheduleSaveAction
  | CheckSaveAction
  | ExecuteSaveAction
  | SaveSuccessAction
  | SaveErrorAction
  | ToggleDeleteAlertAction
  | RequestDeleteAction
  | SetLoadingAction
  | LoadForceAction
  | LoadForceSuccessAction
  | LoadForceNotFoundAction
  | LoadForceErrorAction
  | ResetAction;

/** Create a draft force note */
export const draftForceNote = (slug: string, projectId: string): ForceNote => {
  const now = nowIsoString();
  return {
    slug,
    type: "force",
    title: "",
    content: "",
    tags: [],
    meta: {
      certainty: 0.5,
      impact: 0.5,
    },
    confidence: 0.5,
    source_slugs: [],
    source_urls: [],
    created_at: now,
    updated_at: now,
    project_id: projectId,
  };
};

/** Convert edit form model to ForceNote */
export const toForceNote = (model: Model): ForceNote | undefined => {
  if (model.slug === undefined || model.projectId === undefined) {
    return undefined;
  }

  const now = nowIsoString();

  return {
    slug: model.slug,
    project_id: model.projectId,
    type: model.type,
    title: model.title,
    content: model.description,
    meta: {
      certainty: model.certainty,
      impact: model.impact,
      category: model.category,
      arc: model.arc,
      pace: model.paceLayer,
    },
    tags: model.tags,
    source_slugs: model.sourceSlugs,
    source_urls: model.sourceUrls,
    confidence: model.confidence,
    author_user_id: model.authorUserId,
    author_agent_id: model.authorAgentId,
    created_at: model.createdAt ?? now,
    updated_at: now,
  } as ForceNote;
};

const updateSetForce = (state: Model, action: SetForceAction): Model => {
  const force = action.force;
  return {
    ...state,
    slug: force.slug,
    projectId: force.project_id ?? undefined,
    type: force.type,
    title: force.title,
    description: force.content,
    certainty: force.meta.certainty ?? 0.5,
    impact: force.meta.impact ?? 0.5,
    category: force.meta.category,
    arc: force.meta.arc,
    paceLayer: force.meta.pace,
    tags: force.tags,
    sourceSlugs: force.source_slugs,
    sourceUrls: force.source_urls,
    confidence: force.confidence,
    authorUserId: force.author_user_id ?? undefined,
    authorAgentId: force.author_agent_id ?? undefined,
    createdAt: force.created_at,
    updatedAt: force.updated_at,
    isLoading: false,
  };
};

const updateSetAccessToken = (
  state: Model,
  action: SetAccessTokenAction,
): Model => ({
  ...state,
  accessToken: action.accessToken,
});

const updateSetTitle = (state: Model, action: SetTitleAction): Model => ({
  ...state,
  title: action.value,
});

const updateSetDescription = (
  state: Model,
  action: SetDescriptionAction,
): Model => ({
  ...state,
  description: action.value,
});

const updateSetCertainty = (
  state: Model,
  action: SetCertaintyAction,
): Model => ({
  ...state,
  certainty: action.value,
});

const updateSetImpact = (state: Model, action: SetImpactAction): Model => ({
  ...state,
  impact: action.value,
});

const updateSetCategory = (state: Model, action: SetCategoryAction): Model => ({
  ...state,
  category: action.value,
});

const updateSetArc = (state: Model, action: SetArcAction): Model => ({
  ...state,
  arc: action.value,
});

const updateSetPaceLayer = (
  state: Model,
  action: SetPaceLayerAction,
): Model => ({
  ...state,
  paceLayer: action.value,
});

const updateScheduleSave = (state: Model): Model => ({
  ...state,
  saveToken: Date.now(),
});

const updateExecuteSave = (state: Model): Model => ({
  ...state,
  isSaving: true,
  lastError: undefined,
});

const updateSaveSuccess = (
  state: Model,
  _action: SaveSuccessAction,
): Model => ({
  ...state,
  isSaving: false,
});

const updateSaveError = (state: Model, action: SaveErrorAction): Model => ({
  ...state,
  isSaving: false,
  lastError: action.error,
});

const updateToggleDeleteAlert = (
  state: Model,
  action: ToggleDeleteAlertAction,
): Model => ({
  ...state,
  isConfirmDeleteMenuOpen: action.show,
});

const updateRequestDelete = (state: Model): Model => ({
  ...state,
  isConfirmDeleteMenuOpen: false,
  isDeleting: true,
  lastError: undefined,
});

const updateSetLoading = (state: Model, action: SetLoadingAction): Model => ({
  ...state,
  isLoading: action.isLoading,
});

const updateLoadForce = (state: Model): Model => ({
  ...state,
  isLoading: true,
  lastError: undefined,
});

const updateLoadForceSuccess = (
  state: Model,
  action: LoadForceSuccessAction,
): Model =>
  updateSetForce(
    { ...state, isLoading: false },
    {
      type: "set-force",
      force: action.force,
    },
  );

const updateLoadForceNotFound = (
  state: Model,
  action: LoadForceNotFoundAction,
): Model => {
  // Create a draft force when not found
  const draft = draftForceNote(action.slug, action.projectId);
  return updateSetForce(
    { ...state, isLoading: false },
    {
      type: "set-force",
      force: draft,
    },
  );
};

const updateLoadForceError = (
  state: Model,
  action: LoadForceErrorAction,
): Model => {
  // Create a draft force so the form still appears on API errors
  const draft = draftForceNote(action.slug, action.projectId);
  return updateSetForce(
    { ...state, isLoading: false, lastError: action.error },
    { type: "set-force", force: draft },
  );
};

export const update = (state: Model, action: Action): Model => {
  switch (action.type) {
    case "set-force":
      return updateSetForce(state, action);
    case "set-access-token":
      return updateSetAccessToken(state, action);
    case "set-title":
      return updateSetTitle(state, action);
    case "set-description":
      return updateSetDescription(state, action);
    case "set-certainty":
      return updateSetCertainty(state, action);
    case "set-impact":
      return updateSetImpact(state, action);
    case "set-category":
      return updateSetCategory(state, action);
    case "set-arc":
      return updateSetArc(state, action);
    case "set-pace-layer":
      return updateSetPaceLayer(state, action);
    case "schedule-save":
      return updateScheduleSave(state);
    case "check-save":
      // State doesn't change - effect handles this
      return state;
    case "execute-save":
      return updateExecuteSave(state);
    case "save-success":
      return updateSaveSuccess(state, action);
    case "save-error":
      return updateSaveError(state, action);
    case "toggle-delete-alert":
      return updateToggleDeleteAlert(state, action);
    case "request-delete":
      return updateRequestDelete(state);
    case "set-loading":
      return updateSetLoading(state, action);
    case "load-force":
      return updateLoadForce(state);
    case "load-force-success":
      return updateLoadForceSuccess(state, action);
    case "load-force-not-found":
      return updateLoadForceNotFound(state, action);
    case "load-force-error":
      return updateLoadForceError(state, action);
    case "reset":
      return model();
    default:
      return updateUnknown(state, action);
  }
};

type GetState = () => Model;

async function* fxScheduleSave(
  saveToken: number,
  accessToken: string | undefined,
): AsyncGenerator<Action> {
  // Wait for debounce period
  await sleep(2000);

  // Dispatch check-save with the current model save token
  yield { type: "check-save", saveToken, accessToken };
}

async function* fxCheckSave(
  getState: GetState,
  action: CheckSaveAction,
): AsyncGenerator<Action> {
  const state = getState();

  // Ignore if a newer save was scheduled
  if (state.saveToken > action.saveToken) {
    return;
  }

  const forceNote = toForceNote(state);

  if (forceNote) {
    yield { type: "execute-save", forceNote, accessToken: action.accessToken };
  }
}

export const saveSuccessEvent = customEvent<Slug>("save-success", {
  bubbles: true,
  composed: true,
});

export const saveErrorEvent = customEvent<string>("save-errror", {
  bubbles: true,
  composed: true,
});

async function* fxExecuteSave(
  _getState: GetState,
  action: ExecuteSaveAction,
  host: HTMLElement,
): AsyncGenerator<Action> {
  const { forceNote, accessToken } = action;

  const result = await NotesService.upsertNote(
    accessToken,
    forceNote.project_id,
    forceNote.slug,
    forceNote,
  );

  if (result.ok) {
    yield { type: "save-success", slug: forceNote.slug };
    host.dispatchEvent(saveSuccessEvent(forceNote.slug));
  } else {
    yield { type: "save-error", error: result.error };
    host.dispatchEvent(saveErrorEvent(result.error));
  }
}

async function* fxLoadForce(
  _getState: GetState,
  action: LoadForceAction,
): AsyncGenerator<Action> {
  const { slug, projectId, accessToken } = action;

  const result = await NotesService.getNote(accessToken, projectId, slug);

  if (result.ok) {
    if (result.value) {
      // Found the note - load it
      yield { type: "load-force-success", force: result.value as ForceNote };
    } else {
      // Not found - create draft
      yield { type: "load-force-not-found", slug, projectId };
    }
  } else {
    yield { type: "load-force-error", error: result.error, slug, projectId };
  }
}

export type RequestDeleteNoteDetail = {
  slug: Slug;
  redirectTo?: string;
};

export const requestDeleteNoteEvent = customEvent<RequestDeleteNoteDetail>(
  "request-delete-note",
  {
    bubbles: true,
    composed: true,
  },
);

function dispatchRequestDeleteEvent(
  slug: Slug | undefined,
  projectId: string | undefined,
  host: HTMLElement,
): void {
  if (!slug) {
    log.warn("Can't request delete. No force slug set.");
    return;
  }

  if (!projectId) {
    log.warn("Can't request delete. No project ID set.");
    return;
  }

  const path = renderRoute(Config.PROJECT_URL, {
    project: projectId,
  });

  host.dispatchEvent(
    requestDeleteNoteEvent({
      slug,
      redirectTo: path,
    }),
  );
}

export const fxForceEdit = async function* (
  getState: GetState,
  action: Action,
  host: HTMLElement,
): AsyncGenerator<Action> {
  switch (action.type) {
    case "load-force":
      return yield* fxLoadForce(getState, action);
    case "schedule-save": {
      const state = getState();
      return yield* fxScheduleSave(state.saveToken, action.accessToken);
    }
    case "check-save":
      return yield* fxCheckSave(getState, action);
    case "execute-save":
      return yield* fxExecuteSave(getState, action, host);
    case "request-delete":
      return dispatchRequestDeleteEvent(action.slug, action.projectId, host);
    case "set-arc":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-category":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-certainty":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-description":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-impact":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-pace-layer":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-title":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    default:
      return;
  }
};

export type ForceEditStore = ReturnType<typeof createForceEditStore>;

export const createForceEditStore = (initial: Model, host: HTMLElement) => {
  return pipe(
    store(update, initial),
    logger({
      log: true,
      prefix: "force-edit ",
    }),
    fx(fxForceEdit, host),
  );
};
